import { useMemo, useState } from 'react'
import { Box, Link, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { AlertTriangleIcon, LayoutGridIcon, ListIcon, LockIcon, UsersIcon } from 'lucide-react'
import {
  VERAFIED_CONNECTION_LABELS,
  VERAFIED_EPISODE_STATUS_LABELS,
  VERAFIED_GUEST_STATUS_LABELS,
  VERAFIED_INFLUENCE_LABELS,
  VERAFIED_LINK_KIND_LABELS,
} from '@repo/types'
import { Avatar, Skeleton } from '@repo/ui'
import { Search } from 'components/input/Search'

import { EmptyState, SelectField, Table } from 'components'
import { ViewToggle } from 'components/input/ViewToggle/ViewToggle'
import { useVerafiedGuests } from 'hooks'
import type { VerafiedGuest } from 'services'

import { ToneChip } from '../../InternalAnalyticsPage/components/shared'
import { GuestFormModal } from './GuestFormModal'
import {
  roleLine,
  CONNECTION_OPTIONS,
  EMPTY_GUEST_FILTERS,
  filterGuests,
  GUEST_STATUS_OPTIONS,
  GUEST_STATUS_TONE,
  INFLUENCE_OPTIONS,
  INFLUENCE_TONE,
  initialsOf,
  type GuestFilters,
} from './verafiedModel'

const LAYOUTS = [
  { value: 'cards' as const, label: 'Cards', icon: LayoutGridIcon },
  { value: 'table' as const, label: 'Table', icon: ListIcon },
]

const HAS_EPISODE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'yes', label: 'Has an episode' },
  { value: 'no', label: 'No episode yet' },
]

const asList = (v: string | string[]) => (Array.isArray(v) ? v : v ? [v] : [])

const GuestCard = ({ guest, onOpen }: { guest: VerafiedGuest; onOpen: (g: VerafiedGuest) => void }) => {
  const theme = useTheme()
  const subtitle = roleLine(guest)
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onOpen(guest)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(guest)
      }}
      data-testid={`guest-card-${guest.id}`}
      sx={{
        p: 4,
        borderRadius: 3,
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: 'background.paper',
        cursor: 'pointer',
        minWidth: 0,
        // The action-row pattern (§34): a click opens the guest, so hover = brand border + ring.
        '&:hover, &:focus-visible': {
          borderColor: theme.palette.brand[600],
          boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
          outline: 'none',
        },
      }}
    >
      <Stack spacing={3}>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar src={guest.photo_url} name={guest.name} initials={initialsOf(guest.name)} size={44} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={guest.name}>
              {guest.name}
            </Typography>
            <Typography variant="body3" color="text.secondary" noWrap title={subtitle}>
              {subtitle || (guest.guest_type === 'organisation' ? 'Organisation' : 'No role recorded')}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <ToneChip tone={GUEST_STATUS_TONE[guest.status]} label={VERAFIED_GUEST_STATUS_LABELS[guest.status]} />
          {guest.influence && <ToneChip tone={INFLUENCE_TONE[guest.influence]} label={`${VERAFIED_INFLUENCE_LABELS[guest.influence]} influence`} />}
          {guest.connection && <ToneChip tone="neutral" label={VERAFIED_CONNECTION_LABELS[guest.connection]} />}
          {guest.guest_type === 'organisation' && <ToneChip tone="neutral" label="Organisation" />}
        </Stack>

        {guest.check_required && (
          <Tooltip title={guest.check_required}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: theme.palette.warning.main }}>
              <AlertTriangleIcon size={16} strokeWidth={1.5} />
              <Typography variant="body3" sx={{ fontWeight: 500 }}>
                Check before any invitation
              </Typography>
            </Stack>
          </Tooltip>
        )}
        {guest.import_notes.length > 0 && (
          <Tooltip title={guest.import_notes.join(' ')}>
            <Box>
              <ToneChip tone="caution" label={`Confirm ${guest.import_notes.length === 1 ? 'an import fix' : `${guest.import_notes.length} import fixes`}`} />
            </Box>
          </Tooltip>
        )}

        {guest.episodes.length > 0 && (
          <Stack spacing={1}>
            {guest.episodes.map((e) => (
              <Typography key={e.id} variant="body3" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  {VERAFIED_EPISODE_STATUS_LABELS[e.status]}:
                </Box>{' '}
                {e.title}
              </Typography>
            ))}
          </Stack>
        )}

        {guest.links.length > 0 && (
          <Stack direction="row" spacing={3} useFlexGap sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {guest.links.map((l) => (
              <Link
                key={l.id}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body3"
                underline="hover"
                onClick={(e) => e.stopPropagation()}
              >
                {VERAFIED_LINK_KIND_LABELS[l.kind]}
              </Link>
            ))}
          </Stack>
        )}

        {guest.notes && (
          <Tooltip title="Internal note. Never exported or shown outside these pages.">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ color: 'text.secondary', pt: '2px', flexShrink: 0 }}>
                <LockIcon size={12} strokeWidth={1.5} />
              </Box>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{ lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {guest.notes}
              </Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

const GRID_SX = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 3 }

/** §19 — the card grid's footprint. */
const GuestsSkeleton = () => (
  <Box sx={GRID_SX}>
    {Array.from({ length: 8 }, (_, i) => (
      <Stack key={i} spacing={3} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'primary.100' }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Skeleton variant="circular" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton height={16} width="70%" />
            <Skeleton height={14} width="50%" />
          </Box>
        </Stack>
        <Skeleton height={22} width="80%" />
        <Skeleton lines={2} />
      </Stack>
    ))}
  </Box>
)

/**
 * VRG-734 — VERAfied guests. A guest is a ROLE on a person (or company) we already store; the card
 * shows that record's name when it is linked. Photos are uploaded only, never scraped; the fallback
 * is initials. Notes are internal: shown here, matched by nothing, exported never.
 */
export const GuestsView = ({ canManage, adding, onAddingDone }: { canManage: boolean; adding: boolean; onAddingDone: () => void }) => {
  const { data, isLoading, error } = useVerafiedGuests()
  const [filters, setFilters] = useState<GuestFilters>(EMPTY_GUEST_FILTERS)
  const [layout, setLayout] = useState<'cards' | 'table'>('cards')
  const [editing, setEditing] = useState<VerafiedGuest | null>(null)

  const guests = useMemo(() => data ?? [], [data])
  const rows = useMemo(() => filterGuests(guests, filters), [guests, filters])
  const industryOptions = useMemo(
    () => [...new Set(guests.map((g) => g.industry).filter((v): v is string => !!v))].sort().map((v) => ({ value: v, label: v })),
    [guests],
  )
  const companyTypeOptions = useMemo(
    () => [...new Set(guests.map((g) => g.company_type).filter((v): v is string => !!v))].sort().map((v) => ({ value: v, label: v })),
    [guests],
  )
  const set = <K extends keyof GuestFilters>(k: K, v: GuestFilters[K]) => setFilters((f) => ({ ...f, [k]: v }))
  const filtered = rows.length !== guests.length

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Guest',
        type: 'string',
        flex: 1.3,
        minWidth: 220,
        renderCell: ({ row }: { row: VerafiedGuest }) => (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar src={row.photo_url} name={row.name} initials={initialsOf(row.name)} size={28} />
            <Link component="button" variant="body3" underline="hover" sx={{ fontWeight: 500, textAlign: 'left' }} onClick={() => setEditing(row)} noWrap>
              {row.name}
            </Link>
          </Stack>
        ),
      },
      {
        field: 'company',
        headerName: 'Role · company',
        type: 'string',
        flex: 1.2,
        minWidth: 200,
        valueGetter: (_v: unknown, row: VerafiedGuest) => roleLine(row),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 160,
        valueGetter: (_v: unknown, row: VerafiedGuest) => VERAFIED_GUEST_STATUS_LABELS[row.status],
        renderCell: ({ row }: { row: VerafiedGuest }) => <ToneChip tone={GUEST_STATUS_TONE[row.status]} label={VERAFIED_GUEST_STATUS_LABELS[row.status]} />,
      },
      {
        field: 'influence',
        headerName: 'Influence',
        type: 'string',
        width: 130,
        valueGetter: (_v: unknown, row: VerafiedGuest) => (row.influence ? VERAFIED_INFLUENCE_LABELS[row.influence] : ''),
      },
      {
        field: 'connection',
        headerName: 'How we know them',
        type: 'string',
        width: 180,
        valueGetter: (_v: unknown, row: VerafiedGuest) => (row.connection ? VERAFIED_CONNECTION_LABELS[row.connection] : ''),
      },
      {
        field: 'episodes',
        headerName: 'Episodes',
        type: 'string',
        flex: 1.4,
        minWidth: 220,
        valueGetter: (_v: unknown, row: VerafiedGuest) =>
          row.episodes.map((e) => `${VERAFIED_EPISODE_STATUS_LABELS[e.status]}: ${e.title}`).join('; '),
      },
      {
        field: 'links',
        headerName: 'Profiles',
        type: 'string',
        width: 150,
        valueGetter: (_v: unknown, row: VerafiedGuest) => row.links.map((l) => VERAFIED_LINK_KIND_LABELS[l.kind]).join(', '),
      },
      {
        // SENSITIVE. `disableExport` keeps it out of Download (toCsvRows honours it).
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        flex: 1.2,
        minWidth: 200,
        disableExport: true,
        // Not filterable either: a filter on notes would reveal them by elimination, as search would.
        filterable: false,
        sortable: false,
      },
    ],
    [],
  )

  const countAndToggle = (
    <Stack direction="row" alignItems="center" spacing={3}>
      <ViewToggle options={LAYOUTS} value={layout} onChange={setLayout} ariaLabel="Guest layout" />
      <Typography variant="body3" color="text.secondary">
        {isLoading ? ' ' : filtered ? `${rows.length} of ${guests.length} guests` : `${guests.length} guests`}
      </Typography>
    </Stack>
  )

  return (
    <Stack spacing={4}>
      <Stack direction="row" spacing={3} useFlexGap alignItems="flex-end" sx={{ flexWrap: 'wrap', rowGap: 3 }}>
        <Box sx={{ width: 300 }}>
          <Search value={filters.q} onChange={(v: string) => set('q', v)} onClear={() => set('q', '')} placeholder="Search guests" fullWidth />
        </Box>
        <Box sx={{ width: 140 }}>
          <SelectField multiple placeholder="Status" options={GUEST_STATUS_OPTIONS} value={filters.status} onChange={(v) => set('status', asList(v))} />
        </Box>
        <Box sx={{ width: 130 }}>
          <SelectField multiple placeholder="Influence" options={INFLUENCE_OPTIONS} value={filters.influence} onChange={(v) => set('influence', asList(v))} />
        </Box>
        <Box sx={{ width: 170 }}>
          <SelectField
            multiple
            placeholder="How we know them"
            options={CONNECTION_OPTIONS}
            value={filters.connection}
            onChange={(v) => set('connection', asList(v))}
          />
        </Box>
        <Box sx={{ width: 150 }}>
          <SelectField
            placeholder="Episode"
            options={HAS_EPISODE_OPTIONS}
            value={filters.hasEpisode}
            onChange={(v) => set('hasEpisode', (Array.isArray(v) ? v[0] : v) as GuestFilters['hasEpisode'])}
          />
        </Box>
        {industryOptions.length > 0 && (
          <Box sx={{ width: 140 }}>
            <SelectField multiple placeholder="Industry" options={industryOptions} value={filters.industry} onChange={(v) => set('industry', asList(v))} />
          </Box>
        )}
        {companyTypeOptions.length > 0 && (
          <Box sx={{ width: 150 }}>
            <SelectField
              multiple
              placeholder="Company type"
              options={companyTypeOptions}
              value={filters.companyType}
              onChange={(v) => set('companyType', asList(v))}
            />
          </Box>
        )}
      </Stack>

      {/* Table view: joins the table's toolbar row (see EpisodesView). */}
      {layout !== 'table' && countAndToggle}

      {error ? (
        <EmptyState title="Could not load guests" text={(error as Error).message} icon={<UsersIcon strokeWidth={1} size={32} />} />
      ) : layout === 'table' ? (
        <Table
          loading={isLoading}
          rows={rows}
          columns={columns}
          getRowId={(r: VerafiedGuest) => r.id}
          showSearch={false}
          actionLineLeftComponents={countAndToggle}
          defaultSortModel={[{ field: 'name', sort: 'asc' }]}
          exportFilename="verafied-guests"
          topOffset={420}
          emptyState={<EmptyState title="No guests match" text="Clear the search or a filter." icon={<UsersIcon strokeWidth={1} size={32} />} />}
        />
      ) : isLoading ? (
        <GuestsSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          title={guests.length ? 'No guests match' : 'No guests yet'}
          text={guests.length ? 'Clear the search or a filter.' : 'Import the Guests sheet, or add a guest.'}
          icon={<UsersIcon strokeWidth={1} size={32} />}
        />
      ) : (
        <Box sx={GRID_SX}>
          {rows.map((g) => (
            <GuestCard key={g.id} guest={g} onOpen={setEditing} />
          ))}
        </Box>
      )}

      <GuestFormModal
        open={adding || !!editing}
        guest={editing}
        canManage={canManage}
        onClose={() => {
          setEditing(null)
          onAddingDone()
        }}
      />
    </Stack>
  )
}
